import { AccordionAnimator } from "./accordionanimator";

interface Recall {
    id: string;
    status: string;
    statusMessage: string,
    name: string;
    description: string;
    securityRisk: string;
    measure: string;
    repairDate: string;
}

interface VehicleRecalls {
    vin: string;
    regMark: string;
    model: string;
    color: string;
    recalls: Recall[];
}

interface RecallDictionary {
    action: string;
    color: string;
    completed: string;
    description: string;
    error: string;
    recallFor: string;
    model: string;
    needsAction: string;
    needsActionAsap: string;
    notFound: string;
    repairDate: string;
    search: string;
    securityRisk: string;
}

const emptyDictionary: RecallDictionary = {
    action: "",
    color: "",
    completed: "",
    description: "",
    error: "",
    recallFor: "",
    model: "",
    needsAction: "",
    needsActionAsap: "",
    notFound: "",
    repairDate: "",
    search: "",
    securityRisk: ""
};

function parseDictionary(value: string | undefined): RecallDictionary {
    if (!value) return emptyDictionary;

    try {
        return { ...emptyDictionary, ...JSON.parse(value) };
    } catch {
        return emptyDictionary;
    }
}

function getRecallStatus(recall: any): string {
    if (recall.completed) return "Ok";
    return recall.safetyRecall ? "Error" : "Warning";
}

function getRecallStatusMessage(recall: any, dictionary: RecallDictionary): string {
    if (recall.completed) return dictionary.completed;
    return recall.safetyRecall ? dictionary.needsActionAsap : dictionary.needsAction;
}

async function fetchVehicleRecalls(endpoint: string, identifier: string, culture: string, dictionary: RecallDictionary): Promise<VehicleRecalls | null> {
    const url = `${endpoint}?identifier=${encodeURIComponent(identifier.trim())}&culture=${encodeURIComponent(culture)}`;
    const response = await fetch(url, { headers: { Accept: "application/json" } });

    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Vehicle lookup failed with status ${response.status}`);

    const vehicle = await response.json();
    return {
        vin: vehicle.vin ?? "",
        regMark: vehicle.regMark ?? "",
        model: vehicle.model ?? "",
        color: vehicle.color ?? "",
        recalls: (vehicle.recalls ?? []).map((recall: any): Recall => ({
            id: recall.id ?? "",
            status: getRecallStatus(recall),
            statusMessage: getRecallStatusMessage(recall, dictionary),
            name: recall.name || recall.id || "",
            description: recall.description ?? "",
            securityRisk: recall.risk ?? "",
            measure: recall.remedy ?? "",
            repairDate: recall.repairDate ?? ""
        }))
    };
}

function escapeHtml(value: string): string {
    const div = document.createElement("div");
    div.textContent = value;
    return div.innerHTML;
}

interface StatusIcon {
    viewBox: string;
    filled: boolean;
    body: string;
}

const statusIcons: Record<string, StatusIcon> = {
    error: {
        viewBox: "0 -960 960 960",
        filled: true,
        body: `<path d="m336-307.69 144-144 144 144L652.31-336l-144-144 144-144L624-652.31l-144 144-144-144L307.69-624l144 144-144 144L336-307.69ZM480.13-120q-74.67 0-140.41-28.34-65.73-28.34-114.36-76.92-48.63-48.58-76.99-114.26Q120-405.19 120-479.87q0-74.67 28.34-140.41 28.34-65.73 76.92-114.36 48.58-48.63 114.26-76.99Q405.19-840 479.87-840q74.67 0 140.41 28.34 65.73 28.34 114.36 76.92 48.63 48.58 76.99 114.26Q840-554.81 840-480.13q0 74.67-28.34 140.41-28.34 65.73-76.92 114.36-48.58 48.63-114.26 76.99Q554.81-120 480.13-120Zm-.13-40q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>`
    },
    warning: {
        viewBox: "0 -960 960 960",
        filled: true,
        body: `<path d="M109.23-160 480-800l370.77 640H109.23ZM178-200h604L480-720 178-200Zm319.35-62.65q7.27-7.27 7.27-17.35 0-10.08-7.27-17.35-7.27-7.27-17.35-7.27-10.08 0-17.35 7.27-7.27 7.27-7.27 17.35 0 10.08 7.27 17.35 7.27 7.27 17.35 7.27 10.08 0 17.35-7.27ZM460-344.62h40v-200h-40v200ZM480-460Z"/>`
    },
    ok: {
        viewBox: "0 -960 960 960",
        filled: true,
        body: `<path d="m422.46-323.69 255.85-255.85L650-607.85 422.46-380.31l-114-114L280.15-466l142.31 142.31ZM480.13-120q-74.67 0-140.41-28.34-65.73-28.34-114.36-76.92-48.63-48.58-76.99-114.26Q120-405.19 120-479.87q0-74.67 28.34-140.41 28.34-65.73 76.92-114.36 48.58-48.63 114.26-76.99Q405.19-840 479.87-840q74.67 0 140.41 28.34 65.73 28.34 114.36 76.92 48.63 48.58 76.99 114.26Q840-554.81 840-480.13q0 74.67-28.34 140.41-28.34 65.73-76.92 114.36-48.58 48.63-114.26 76.99Q554.81-120 480.13-120Zm-.13-40q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>`
    }
};

function getStatusIcon(status: string): string {
    const key = status.trim().toLowerCase();
    const icon = statusIcons[key];
    if (!icon) return "";

    const paint = icon.filled
        ? `fill="currentColor"`
        : `fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"`;

    return `
        <svg class="recall-status recall-status-${key}" width="24" height="24" viewBox="${icon.viewBox}" ${paint} role="img" aria-label="${escapeHtml(status)}">
            <title>${escapeHtml(status)}</title>
            ${icon.body}
        </svg>
    `;
}

function renderRecallRow(label: string, value: string): string {
    if (!value) return "";

    return `
        <tr>
            <td style="font-weight: 900;">${escapeHtml(label)}</td>
            <td>${escapeHtml(value)}</td>
        </tr>
    `;
}

function renderVehicleRecalls(container: HTMLElement, vehicle: VehicleRecalls | null, dictionary: RecallDictionary): void {
    if (!vehicle) {
        container.innerHTML = `<p>${escapeHtml(dictionary.notFound)}</p>`;
        return;
    }

    const vehicleHtml = `
        <div class="recall-vehicle-info flex flex-col items-center mb-8">
            <h2 class="mb-4">${escapeHtml(dictionary.recallFor)} ${escapeHtml(vehicle.regMark.trim() || vehicle.vin)}</h2>
            <div class="flex flex-wrap gap-4">
                <p><strong>${escapeHtml(dictionary.model)}:</strong> ${escapeHtml(vehicle.model)}</p>
                <p><strong>${escapeHtml(dictionary.color)}:</strong> ${escapeHtml(vehicle.color)}</p>
            </div>

        </div>
    `;

    if (vehicle.recalls.length === 0) {
        container.innerHTML = `${vehicleHtml}<p>${escapeHtml(dictionary.notFound)}</p>`;
        return;
    }

    container.innerHTML = vehicleHtml + vehicle.recalls.map(recall => `
        <details class="accordion border-b border-neutral">
            <summary class="flex justify-between items-center cursor-pointer py-4">

                <div class="flex flex-col items-start gap-2 md:gap-0 w-full md:flex-row md:items-center md:justify-between">
                    <span class="h4 pr-4 inline-block">${escapeHtml(recall.name)}</span>
                    <span class="recall-status-message ${escapeHtml(recall.status.trim().toLowerCase())}">
                        ${getStatusIcon(recall.status)}
                        <span>${escapeHtml(recall.statusMessage)}</span>
                    </span>
                </div>

                <span class="open text-black">
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.5 12.8262H6V11.8262H11.5V6.32617H12.5V11.8262H18V12.8262H12.5V18.3262H11.5V12.8262Z" fill="currentColor"></path>
                    </svg>
                </span>
                <span class="close text-black">
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 13.0449V12.0449H18V13.0449H6Z" fill="currentColor"></path>
                    </svg>
                </span>
            </summary>
            <div style="height: 0px;">
                <div class="py-4 recall-table">
                    <table class="table-responsive-md" border="0">
                        <tbody>
                            ${renderRecallRow("ID", recall.id)}
                            ${renderRecallRow(dictionary.description, recall.description)}
                            ${renderRecallRow(dictionary.securityRisk, recall.securityRisk)}
                            ${renderRecallRow(dictionary.action, recall.measure)}
                            ${renderRecallRow(dictionary.repairDate, recall.repairDate)}
                        </tbody>
                    </table>
                </div>
            </div>
        </details>
    `).join("");

    new AccordionAnimator({
        root: container,
        duration: 200,
        easing: "ease-in-out"
    });
}

export function initRecall(): void {
    document.querySelectorAll<HTMLElement>('[data-jsid="recall"]').forEach(recall => {
        const input = recall.querySelector<HTMLInputElement>("#vin");
        const button = recall.querySelector<HTMLButtonElement>("#search-vin");
        const results = recall.querySelector<HTMLElement>(".recall-vin-results");
        const loader = recall.querySelector<HTMLElement>(".recall-loader");
        const endpoint = recall.dataset.endpoint;
        const culture = recall.dataset.culture ?? "";
        const dictionary = parseDictionary(recall.dataset.dictionary);

        if (!input || !button || !results || !endpoint) return;

        button.addEventListener("click", async () => {
            const vin = input.value.trim();
            if (!vin) return;

            button.disabled = true;
            results.innerHTML = "";
            if (loader) loader.hidden = false;
            try {
                const vehicle = await fetchVehicleRecalls(endpoint, vin, culture, dictionary);
                renderVehicleRecalls(results, vehicle, dictionary);
            } catch {
                results.innerHTML = `<p>${escapeHtml(dictionary.error)}</p>`;
            } finally {
                if (loader) loader.hidden = true;
                button.disabled = false;
            }
        });

        input.addEventListener("keydown", (event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            button.click();
        });
    });
}
